import { getTypeToIconMap } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import IconButton from "components/IconButton";
import { Popover } from "components/Popover/Popover";
import RecordLabel from "components/RecordLabel";
import { getLoading, getUploads } from "containers/Upload/Upload.selectors";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getRelativeUrl } from "utils/service";
import styles from "./UploadPopover.module.scss";

export const UploadPopover = () => {
    const loading = useSelector(getLoading);
    const uploads = useSelector(getUploads);

    const [visible, setVisible] = useState(false);

    // Only auto-unfold the popover once an upload is actually in progress (a
    // file has been chosen AND the transfer has started), not on the premature
    // `loading` flag the file-picker raises while it reads the just-selected
    // files. An in-progress upload is an entry still awaiting a result
    // (`success === null`), which only exists after START_UPLOAD, i.e. after the
    // user confirms the destination in the location dialog. This keeps the
    // popover from stealing focus from the location-selection dialog.
    const hasUploadInProgress = useMemo(
        () => uploads.some((upload) => upload.success === null),
        [uploads]
    );

    useEffect(() => {
        if (hasUploadInProgress) {
            const timer = setTimeout(() => setVisible(true), 100);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [hasUploadInProgress]);

    const popup = useMemo(() => {
        if (!uploads) {
            return;
        }
        const entries = uploads.map((upload, index) => {
            let icon = upload.success
                ? getTypeToIconMap().get(upload.recordType)
                : IconCollection.alertCircleOutline;
            if (upload.success === null) icon = null;
            return (
                <RecordLabel
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${upload.uploadId}-${upload.recordId}-${upload.filename}-${index}`}
                    link={
                        upload.recordId && upload.recordType
                            ? getRelativeUrl(upload.recordType, upload.recordId)
                            : null
                    }
                    name={upload.recordName ? upload.recordName : upload.fileName}
                    icon={icon}
                    loading={upload.success === null}
                    onClick={() => setVisible(false)}
                    tooltipOverlayStyle={{ zIndex: 1300 }}
                />
            );
        });
        return uploads.length > 0 ? entries : "Loading ...";
    }, [uploads]);

    if (!loading && uploads.length <= 0) return null;
    if (uploads.every((entry) => entry.success === false)) return null;

    return (
        <Popover
            isOpen={visible}
            trigger={<IconButton icon={IconCollection.uploadOutline} loading={loading} />}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            onBlur={() => setVisible(false)}
            actionOnOpen={() => setVisible(true)}
            contentClassName={styles.uploadFilePopover}
        >
            {popup}
        </Popover>
    );
};
